export class BrowserLogger {
    constructor(options) {
        var _a, _b, _c, _d;
        this.buffer = [];
        this.flushTimer = null;
        this.defaultContext = {};
        this.endpoint = options.endpoint;
        this.serviceName = options.serviceName;
        this.flushInterval = (_a = options.flushIntervalMs) !== null && _a !== void 0 ? _a : 5000;
        this.maxBufferSize = (_b = options.maxBufferSize) !== null && _b !== void 0 ? _b : 50;
        this.enabled = (_c = options.enabled) !== null && _c !== void 0 ? _c : true;
        this.development = (_d = options.development) !== null && _d !== void 0 ? _d : inferDevelopment();
    }
    setDefaultContext(context) {
        const filtered = {};
        Object.entries(context).forEach(([key, value]) => {
            if (value) {
                filtered[key] = value;
            }
        });
        this.defaultContext = filtered;
    }
    clone(context = {}) {
        const child = new BrowserLogger({
            endpoint: this.endpoint,
            serviceName: this.serviceName,
            flushIntervalMs: this.flushInterval,
            maxBufferSize: this.maxBufferSize,
            enabled: this.enabled,
            development: this.development,
        });
        child.defaultContext = {
            ...this.defaultContext,
            ...context,
            labels: mergeLabels(this.defaultContext.labels, context.labels),
        };
        return child;
    }
    trace(message, context) {
        this.debug(message, context);
    }
    debug(message, context) {
        this.log('debug', message, context);
    }
    info(message, context) {
        this.log('info', message, context);
    }
    warn(message, context) {
        this.log('warn', message, context);
    }
    error(message, context) {
        this.log('error', message, context);
    }
    async flush() {
        if (!this.enabled || !this.endpoint || this.buffer.length === 0) {
            return;
        }
        const payload = this.buffer;
        this.buffer = [];
        try {
            await fetch(this.endpoint, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ logs: payload }),
                keepalive: true,
            });
        }
        catch (error) {
            // Requeue logs on failure (best effort)
            this.buffer.unshift(...payload);
            if (this.development) {
                console.error('[BrowserLogger] Failed to flush logs', error);
            }
        }
    }
    log(level, message, context) {
        if (!this.enabled) {
            return;
        }
        const entry = {
            '@timestamp': new Date().toISOString(),
            'log.level': level,
            message,
            'service.name': this.serviceName,
            'service.environment': getEnvironment(),
        };
        const combinedContext = {
            ...this.defaultContext,
            ...context,
            labels: mergeLabels(this.defaultContext.labels, context === null || context === void 0 ? void 0 : context.labels),
        };
        if (Object.keys(combinedContext).length > 0) {
            if (combinedContext.labels === undefined) {
                delete combinedContext.labels;
            }
            entry.context = combinedContext;
        }
        this.buffer.push(entry);
        if (this.development && typeof console !== 'undefined') {
            const consoleMethod = console[level] || console.log;
            consoleMethod(`[${this.serviceName}] ${message}`, context);
        }
        if (this.buffer.length >= this.maxBufferSize) {
            void this.flush();
            return;
        }
        if (!this.flushTimer) {
            this.flushTimer = setTimeout(() => {
                this.flushTimer = null;
                void this.flush();
            }, this.flushInterval);
        }
    }
}
function mergeLabels(defaults, overrides) {
    const defaultLabels = isRecord(defaults) ? defaults : {};
    const overrideLabels = isRecord(overrides) ? overrides : {};
    const labels = { ...defaultLabels, ...overrideLabels };
    return Object.keys(labels).length > 0 ? labels : undefined;
}
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function getEnvironment() {
    var _a;
    if (typeof process !== 'undefined' && ((_a = process.env) === null || _a === void 0 ? void 0 : _a.NODE_ENV)) {
        return process.env.NODE_ENV;
    }
    if (typeof window !== 'undefined') {
        return window.__ENVIRONMENT__ || undefined;
    }
    return undefined;
}
function inferDevelopment() {
    var _a;
    if (typeof process !== 'undefined' && ((_a = process.env) === null || _a === void 0 ? void 0 : _a.NODE_ENV)) {
        return process.env.NODE_ENV !== 'production';
    }
    return true;
}
